import { youtubeEmbedUrl, type ClipFormat } from "@kmstories/shared";
import { useState } from "react";
import { useT } from "../i18n/LocaleProvider.js";
import { EmailCapture } from "./EmailCapture.js";

/**
 * Click-to-load YouTube facade. Nothing from Google loads until the visitor
 * presses play (GDPR on an EU server, and faster pages). With no videoId it is
 * the "trailer coming" poster with a notify-me field — when the trailer
 * exists, setting the id turns it into the player with zero redesign.
 */
export function TrailerFacade({
  youtubeId,
  format = "video",
  title,
  thumbnailUrl,
  notifySource = "trailer",
}: {
  youtubeId?: string | null;
  format?: ClipFormat;
  title?: string;
  thumbnailUrl?: string | null;
  notifySource?: "trailer";
}) {
  const { m } = useT();
  const [playing, setPlaying] = useState(false);

  if (!youtubeId) {
    return (
      <div className="facade" role="region" aria-label={m.trailer.heading}>
        {thumbnailUrl && <img className="facade__thumb" src={thumbnailUrl} alt="" />}
        <div className="facade__scrim" />
        <div className="facade__play" aria-hidden="true">
          <span className="facade__glyph facade__glyph--dormant">
            <PlayIcon />
          </span>
        </div>
        <div className="facade__caption">
          <p className="facade__title">{m.trailer.comingTitle}</p>
          <p className="muted small" style={{ margin: 0 }}>
            {m.trailer.comingBody}
          </p>
          <EmailCapture source={notifySource} compact />
        </div>
      </div>
    );
  }

  if (playing) {
    return (
      <div className={`facade ${format === "short" ? "facade--short" : ""}`}>
        <iframe
          src={youtubeEmbedUrl(youtubeId)}
          title={title ?? m.trailer.heading}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          loading="lazy"
        />
      </div>
    );
  }

  return (
    <div className={`facade ${format === "short" ? "facade--short" : ""}`}>
      {thumbnailUrl && <img className="facade__thumb" src={thumbnailUrl} alt="" />}
      <div className="facade__scrim" />
      <button type="button" className="facade__play" onClick={() => setPlaying(true)} aria-label={`${m.trailer.playClip}: ${title ?? ""}`}>
        <span className="facade__glyph">
          <PlayIcon />
        </span>
      </button>
      <div className="facade__caption" aria-hidden="true">
        {title && <p className="facade__title">{title}</p>}
        <p className="muted small" style={{ margin: 0 }}>
          {m.trailer.loadsFromYouTube}
        </p>
      </div>
    </div>
  );
}

function PlayIcon() {
  return (
    <svg className="flip-in-rtl" width="28" height="28" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="currentColor" d="M8 5v14l11-7z" />
    </svg>
  );
}
